import React from 'react';
import { styled } from '@linaria/react';

import VideoGif from '@/components/VideoGif';

import ScreenTip from './ScreenTip';

function HelpScreen({ handleDismiss, ...delegated }) {
  React.useEffect(() => {
    function handleKeydown(event) {
      if (event.code === 'Escape') {
        handleDismiss();
      }
    }

    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [handleDismiss]);

  return (
    <Wrapper {...delegated}>
      <Tip />
      <Grid>
        <MobileColumn style={{ '--delay': '350ms' }}>
          <VideoGif
            includeBorder={false}
            width={325}
            height={325}
            wrapperStyle={{
              margin: '18px auto',
            }}
            src="/images/interactive-guide-to-grid/assignable-children-pointer.mp4"
          />
          <p>
            Tap a single cell to place the child within. Press and
            drag to span the child across multiple rows and/or
            columns.
          </p>
        </MobileColumn>
        <DesktopColumn style={{ '--delay': '350ms' }}>
          <h4>With a pointer device</h4>
          <VideoGif
            includeBorder={false}
            width={325}
            height={325}
            wrapperStyle={{
              margin: '18px auto',
            }}
            src="/images/interactive-guide-to-grid/assignable-children-pointer.mp4"
          />
          <p>
            Click a single cell to place the child within. Click and
            drag to span the child across multiple rows and/or
            columns.
          </p>
        </DesktopColumn>
        <Divider style={{ '--delay': '380ms' }} />
        <DesktopColumn style={{ '--delay': '420ms' }}>
          <h4>With keyboard</h4>
          <VideoGif
            includeBorder={false}
            width={325}
            height={325}
            wrapperStyle={{
              margin: '18px auto',
            }}
            src="/images/interactive-guide-to-grid/assignable-children-keyboard.mp4"
          />
          <p>
            Tab to focus the first cell, and then use arrow keys to
            move around. Press <kbd>Enter</kbd> to enter “placement
            mode”. Press <kbd>Enter</kbd> again to lock in the
            selected placement.
          </p>
        </DesktopColumn>
      </Grid>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  --inset: 8px;
  position: relative;
  margin: calc(var(--inset) * -1);
  padding: 16px 24px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
  background: var(--syntax-bg);
  animation: fadeIn 500ms cubic-bezier(0.54, 0.18, 0.02, 1);

  @media (max-width: 750px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 16px;
  }

  h4 {
    font-size: 1.125rem;
    font-weight: var(--font-weight-bold);
    text-align: center;
  }

  p {
    text-align: center;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2px 1fr;
  gap: 24px;

  @media (max-width: 750px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const Tip = styled(ScreenTip)`
  width: 35px;
  position: absolute;
  top: -21.5px;
  left: 70px;
  animation: bulge 500ms cubic-bezier(0.18, 0.57, 0.44, 1.1);
  transform-origin: 50% 78%;

  @keyframes bulge {
    from {
      transform: scaleY(0.25);
    }
  }
`;

const Column = styled.div`
  animation: contentRise 300ms both cubic-bezier(0, 0.33, 0.41, 1.01);
  animation-delay: var(--delay);

  @keyframes contentRise {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
  }
`;

const MobileColumn = styled(Column)`
  display: none;

  @media (max-width: 750px) {
    display: revert;
  }
`;
const DesktopColumn = styled(Column)`
  @media (max-width: 750px) {
    display: none;
  }
`;

const Divider = styled.div`
  border-left: 1px solid var(--color-gray-100);
  animation: fadeIn 500ms cubic-bezier(0.54, 0.18, 0.02, 1);

  @media (max-width: 750px) {
    display: none;
  }
`;

export default HelpScreen;
